/**
 * The last wall before a generic relationship surface creates a human (#1075).
 *
 * `RelationAdd` searches first, but its create affordance can still be chosen
 * while that search is showing matches. The docked form therefore repeats the
 * governed directory search immediately before the registered relationship
 * create verb. A search
 * error is not an empty directory, and a candidate is not consent to link the
 * first person with that name.
 *
 * Mutation record (2026-08-22):
 *
 * | mutation | named red |
 * |---|---|
 * | bypass the `core_person` search | existing person returned `create`, not `review` |
 * | turn a search rejection into `[]` | refusal test resolved instead of rejecting |
 * | accept any old review fingerprint | edited identity returned `create`, not a fresh review |
 * | apply the guard to every entity | organization quick-create called person search |
 */
import type { LookupItem, LookupSection } from '@solum/ui'
import type { Row } from '@solum/sdk'

export type PersonDirectorySearch = (
  query: string, facet: string | null, type: string | null,
) => Promise<LookupSection[]>

export interface PersonCreateReview {
  kind: 'review'
  query: string
  fingerprint: string
  candidates: LookupItem[]
}

export type PersonCreateDecision = { kind: 'create'; fingerprint: string } | PersonCreateReview

const text = (value: unknown): string => typeof value === 'string' ? value.trim() : ''

/** The identity fields whose edit invalidates an earlier duplicate review. */
export function personIdentityFingerprint(draft: Row): string {
  return JSON.stringify({
    display_name: text(draft.display_name),
    given_name: text(draft.given_name),
    family_name: text(draft.family_name),
  })
}

/** The whole-directory query, preferring the explicit display name when set. */
export function personDirectoryQuery(draft: Row): string {
  const display = text(draft.display_name)
  if (display) return display
  return [text(draft.given_name), text(draft.family_name)].filter(Boolean).join(' ')
}

/**
 * Decide whether the existing atomic create may run.
 *
 * Only `core_person` is special: other registered entities retain generic
 * quick-create. `approvedFingerprint` is the explicit "different person"
 * choice; it is valid only for the exact identity the user reviewed.
 */
export async function decideRelatedCreate(
  entity: string,
  draft: Row,
  search: PersonDirectorySearch,
  approvedFingerprint?: string,
): Promise<PersonCreateDecision> {
  const fingerprint = personIdentityFingerprint(draft)
  if (entity !== 'core_person' || approvedFingerprint === fingerprint) {
    return { kind: 'create', fingerprint }
  }

  const query = personDirectoryQuery(draft)
  if (!query) throw new Error('A person needs a name before the directory can be checked.')

  // Deliberately not caught: unavailable/refused search is not evidence of no
  // match, so the caller must fail closed and create nothing.
  const sections = await search(query, null, null)
  const candidates = sections.flatMap((section) => section.items)
  return candidates.length === 0
    ? { kind: 'create', fingerprint }
    : { kind: 'review', query, fingerprint, candidates }
}
